import React, { useState } from 'react';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { useLang } from '../contexts/LanguageContext';
import { api } from '../lib/api';

export default function AIChat() {
  const { t, lang, isRTL } = useLang();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [diet, setDiet] = useState('any');
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');

  const submit = async () => {
    setLoading(true);
    setReply('');
    try {
      const ingredients = note.split(/[,،+]/).map(s => s.trim()).filter(Boolean);
      const { data } = await api.post('/ai/suggest', {
        ingredients,
        diet: diet === 'any' ? '' : diet,
        language: lang,
        note,
      });
      setReply(data.suggestion);
    } catch (e) {
      setReply(e?.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="ai-chat-open"
          className="fixed end-5 z-40 inline-flex items-center gap-2 rounded-full px-5 py-3 bg-[#7A8B76] text-white shadow-lg hover:bg-[#62725f] transition-all hover:-translate-y-0.5"
          style={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom))' }}
        >
          <Sparkles className="h-5 w-5" />
          <span className="font-medium text-sm">{t('aiAssistant')}</span>
        </button>
      </SheetTrigger>
      <SheetContent side={isRTL ? 'left' : 'right'} className="bg-[#F1F4EE] w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#7A8B76]" /> {t('aiAssistant')}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-6 flex flex-col gap-4 h-[80vh]">
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#5C4D44] mb-2">{t('diet')}</label>
            <Select value={diet} onValueChange={setDiet}>
              <SelectTrigger data-testid="ai-diet-select" className="bg-white border-black/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t('dietAny')}</SelectItem>
                <SelectItem value="keto">{t('dietKeto')}</SelectItem>
                <SelectItem value="vegan">{t('dietVegan')}</SelectItem>
                <SelectItem value="healthy">{t('dietHealthy')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 overflow-y-auto rounded-2xl bg-white border border-black/5 p-4">
            {reply ? (
              <pre className="whitespace-pre-wrap text-sm text-[#2C2420] leading-relaxed font-sans">{reply}</pre>
            ) : loading ? (
              <div className="flex items-center gap-2 text-[#5C4D44]"><Loader2 className="h-4 w-4 animate-spin" /> {t('aiThinking')}</div>
            ) : (
              <p className="text-sm text-[#5C4D44] leading-relaxed">{t('voiceHelp')}</p>
            )}
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('askPlaceholder')}
            data-testid="ai-prompt-input"
            className="resize-none bg-white border-black/10"
            rows={3}
          />
          <Button
            onClick={submit}
            disabled={loading}
            data-testid="ai-send-btn"
            className="rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Send className="h-4 w-4 me-2" />}
            {t('send')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
